import { FC, useState, useEffect } from "react";
import { Button, TextField, Box, CircularProgress } from "@mui/material";
import { proposeTranslationEditAndRefresh } from "../../store/slices/translationsSlice";
import { useAppDispatch, useAppSelector } from "../../hooks/redux";
import { selectEditingLoading } from "../../store/selectors/translationsSelectors";

interface EditTranslationProps {
    currentDetails: {
        translationRecordId: string;
        language: string;
    };
    handleCloseEditTranslation: () => void;
    onChangesPending: (hasPendingChanges: boolean) => void;
}

const styles = {
    container: {
        width: 'match-parent',
        padding: '24px',
        backgroundColor: '#FAFAFA',
        border: '1px solid #bdbdbd',
        borderRadius: '6px',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        marginTop: '16px'
    },
    fieldsContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px'
    },
    buttonsContainer: {
        display: 'flex',
        flexDirection: 'row',
        gap: '12px'
    }
};

export const EditTranslation: FC<EditTranslationProps> = ({ currentDetails, handleCloseEditTranslation, onChangesPending }) => {
    const [updatedTranslation, setUpdatedTranslation] = useState('');
    const [changeReason, setChangeReason] = useState('');
    const dispatch = useAppDispatch();
    const isLoading = useAppSelector(selectEditingLoading);
    
    const isSubmitDisabled = !updatedTranslation.trim() || !changeReason.trim();

    const handleSubmit = async () => {
        const postData = {
            translationRecordId: currentDetails.translationRecordId,
            updatedTranslation: updatedTranslation.trim(),
            changeReason: changeReason.trim(),
            language: currentDetails.language
        };
        await dispatch(proposeTranslationEditAndRefresh(postData));
        handleResetEditTranslation();
    };

    const handleResetEditTranslation = () => {
        setUpdatedTranslation('');
        setChangeReason('');
        onChangesPending(false);
        handleCloseEditTranslation();
    };

    useEffect(() => {
        // Check if both fields have content
        const hasPendingChanges = Boolean(updatedTranslation.trim() && changeReason.trim());
        onChangesPending(hasPendingChanges);
    }, [updatedTranslation, changeReason, onChangesPending]);

    return (
        <Box sx={styles.container}>
            <Box sx={styles.fieldsContainer}>
                <TextField 
                    id="updated-translation" 
                    label="*Updated translation" 
                    variant="outlined" 
                    fullWidth
                    value={updatedTranslation}
                    onChange={(e) => setUpdatedTranslation(e.target.value)}
                    disabled={isLoading}
                />
                <TextField 
                    id="change-reason" 
                    label="*Change reason" 
                    variant="outlined" 
                    fullWidth
                    value={changeReason}
                    onChange={(e) => setChangeReason(e.target.value)}
                    disabled={isLoading}
                />
            </Box>
            <Box sx={styles.buttonsContainer}>
                <Button 
                    variant="contained" 
                    sx={{ flex: 1 }} 
                    disabled={isSubmitDisabled || isLoading}
                    onClick={handleSubmit}
                    startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : undefined}
                >
                    {isLoading ? 'SUBMITTING...' : 'SUBMIT NEW TRANSLATION'}
                </Button>
                <Button variant="outlined" sx={{ flex: 1 }} onClick={handleCloseEditTranslation} disabled={isLoading}>Cancel</Button>
            </Box>
        </Box>
    );
}
